/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { createColumnHelper } from '@tanstack/table-core';
import MarketingSourceLogo from 'common/components/icons/marketingSourceLogo';
import { MetricTypes } from 'common/constants/metric-types';
import { MetricReport, MetricValue } from './types/metric-report';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';
import { StickyOptions } from 'common/components/table/tableTypes';
import Blur from 'features/blur/blur';
import { Row } from '@tanstack/react-table';
import { UseReportQueriesResult } from './hooks/use-report-queries';
import Spinner, { SpinnerSize } from 'common/components/loading/spinner';
import { AdvancedReportTableRow } from 'features/advanced-reports/table/advanced-reports-table.types';

function setZeroAsDefault(value: any): any {
  if (!value) {
    return 0;
  }

  return value;
}

function setDashAsDefaultAfterFormat(value: string): string {
  if (!value || value === '0') {
    return '-';
  }

  return value;
}

function getFieldCell(cellContent: string): any {
  return (
    <div className="flex items-center">
      <MarketingSourceLogo
        marketingSource={cellContent}
        iconClasses="text-sm flex mr-2"
        imageClasses="h-3 mr-2"
      />
      <span className="text-sm truncate block">{cellContent}</span>
    </div>
  );
}

const reportColumnHelper = createColumnHelper<AdvancedReportTableRow>();

function createHeader(headerName: string, isLoading: boolean) {
  return (
    <div className="flex items-center">
      <span className="relative">
        {headerName}
        {isLoading ? (
          <div className="absolute -right-5 top-1/2 -translate-y-1/2 bg-white">
            <Spinner size={SpinnerSize.SMALL} />
          </div>
        ) : (
          <></>
        )}
      </span>
    </div>
  );
}

const defaultColumnWidth = 100;

export const getMetricReportColumns = (
  totalsMetricReport: AdvancedReportTableRow,
  shouldBlurFieldColumn: boolean,
  displayComparisonAttributionModelData: boolean,
  reportQueryResults: UseReportQueriesResult,
  currencyCode?: string
) => {
  const isMainImpressionsDataFetching =
    reportQueryResults.main.impressions.isFetching;
  const isMainClicksDataFetching = reportQueryResults.main.clicks.isFetching;
  const isMainCallsDataFetching = reportQueryResults.main.calls.isFetching;
  const isMainFormsDataFetching = reportQueryResults.main.forms.isFetching;
  const isMainOppsDataFetching = reportQueryResults.main.opps.isFetching;
  const isMainOppsValueDataFetching =
    reportQueryResults.main.oppValues.isFetching;
  const isMainSalesDataFetching = reportQueryResults.main.sales.isFetching;
  const isMainRevenueDataFetching = reportQueryResults.main.revenues.isFetching;
  const isMainCostDataFetching = reportQueryResults.main.cost.isLoading;
  const isCompareImpressionsDataFetching =
    reportQueryResults.compare.impressions.isFetching;
  const isCompareClicksDataFetching =
    reportQueryResults.compare.clicks.isFetching;
  const isCompareCallsDataFetching =
    reportQueryResults.compare.calls.isFetching;
  const isCompareFormsDataFetching =
    reportQueryResults.compare.forms.isFetching;
  const isCompareOppsDataFetching = reportQueryResults.compare.opps.isFetching;
  const isCompareOppsValueDataFetching =
    reportQueryResults.compare.oppValues.isFetching;
  const isCompareSalesDataFetching =
    reportQueryResults.compare.sales.isFetching;
  const isCompareRevenueDataFetching =
    reportQueryResults.compare.revenues.isFetching;
  const isCompareCostDataFetching = reportQueryResults.compare.cost.isFetching;

  return [
    reportColumnHelper.accessor((row) => row.field, {
      id: 'Field',
      header: () => <span>Field</span>,
      enableColumnFilter: false,
      cell: (info) =>
        shouldBlurFieldColumn ? (
          <Blur>{getFieldCell(info.getValue())}</Blur>
        ) : (
          getFieldCell(info.getValue())
        ),
      meta: {
        stickyOptions: {
          isEnabled: true,
          side: 'left'
        } as StickyOptions
      },
      enableGlobalFilter: true,
      minSize: 200
    }),
    reportColumnHelper.accessor((row) => row.impressions, {
      id: MetricTypes.IMPRESSIONS,
      header: () =>
        createHeader(
          'Impressions',
          isMainImpressionsDataFetching || isCompareImpressionsDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm truncate block">
            {setDashAsDefaultAfterFormat(
              Intl.NumberFormat('en-GB', {
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))
            )}
          </span>
          {displayComparisonAttributionModelData ? (
            <>
              <span className="text-xs truncate block">
                {setDashAsDefaultAfterFormat(
                  Intl.NumberFormat('en-GB', {
                    maximumFractionDigits: 2
                  }).format(setZeroAsDefault(info.getValue().compareValue))
                )}
              </span>
            </>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.clicks, {
      id: MetricTypes.CLICKS,
      header: () =>
        createHeader(
          'Clicks',
          isMainClicksDataFetching || isCompareClicksDataFetching
        ),
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => (
        <>
          <span className="text-sm truncate block">
            {Intl.NumberFormat('en-GB', {
              maximumFractionDigits: 2
            }).format(setZeroAsDefault(info.getValue().primaryValue))}
          </span>
          {displayComparisonAttributionModelData ? (
            <>
              <span className="text-xs truncate block">
                {Intl.NumberFormat('en-GB', {
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </span>
            </>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.call, {
      id: MetricTypes.INBOUND_CALL,
      header: () =>
        createHeader(
          'Calls',
          isMainCallsDataFetching || isCompareCallsDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm truncate block">
            {Intl.NumberFormat('en-GB', {
              maximumFractionDigits: 2
            }).format(setZeroAsDefault(info.getValue().primaryValue))}
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs truncate block">
              {Intl.NumberFormat('en-GB', {
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().compareValue))}
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.web, {
      id: MetricTypes.WEB,
      header: () =>
        createHeader(
          'Forms',
          isMainFormsDataFetching || isCompareFormsDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <span className="truncate">
          <span className="text-sm">
            {Intl.NumberFormat('en-GB', {
              maximumFractionDigits: 2
            }).format(setZeroAsDefault(info.getValue().primaryValue))}
          </span>
          {info.row.original.field !== 'Total' ? (
            <span className="text-xs ml-1 leading-5">
              {toPercentage(
                +info.getValue(),
                +totalsMetricReport.web.primaryValue
              )}
            </span>
          ) : (
            <></>
          )}
          {displayComparisonAttributionModelData ? (
            <>
              <br />
              <span className="text-xs">
                {Intl.NumberFormat('en-GB', {
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </span>
            </>
          ) : (
            <></>
          )}
        </span>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.opportunity, {
      id: MetricTypes.OPPORTUNITIES,
      header: () =>
        createHeader(
          'Opps',
          isMainOppsDataFetching || isCompareOppsDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <span className="truncate">
          <span className="text-sm">
            {Intl.NumberFormat('en-GB', {
              maximumFractionDigits: 2
            }).format(setZeroAsDefault(info.getValue().primaryValue))}
          </span>
          {displayComparisonAttributionModelData ? (
            <>
              <br />
              <span className="text-xs">
                {Intl.NumberFormat('en-GB', {
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </span>
            </>
          ) : (
            <></>
          )}
          {info.row.original.field !== 'Total' ? (
            <span className="text-xs ml-1 leading-5">
              {toPercentage(
                +info.getValue(),
                +totalsMetricReport.opportunity.primaryValue
              )}
            </span>
          ) : (
            <></>
          )}
        </span>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.opportunityValue, {
      id: MetricTypes.OPPORTUNITY_VALUES,
      header: () =>
        createHeader(
          'Opps Values',
          isMainOppsValueDataFetching || isCompareOppsValueDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm">
            <CurrencyDisplayWrapper currencyCode={currencyCode}>
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))}
            </CurrencyDisplayWrapper>
          </span>
          {displayComparisonAttributionModelData ? (
            <>
              <span className="text-xs">
                <CurrencyDisplayWrapper currencyCode={currencyCode}>
                  {Intl.NumberFormat('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(setZeroAsDefault(info.getValue().compareValue))}
                </CurrencyDisplayWrapper>
              </span>
            </>
          ) : (
            <></>
          )}
        </>
      ),
      size: 110
    }),
    reportColumnHelper.accessor((row) => row.sales, {
      id: MetricTypes.REVENUES,
      header: () =>
        createHeader(
          'Sales',
          isMainSalesDataFetching || isCompareSalesDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <span className="truncate">
          <span className="text-sm">
            {Intl.NumberFormat('en-GB', {
              maximumFractionDigits: 2
            }).format(setZeroAsDefault(info.getValue().primaryValue))}
          </span>
          {info.row.original.field !== 'Total' ? (
            <span className="text-xs ml-1 leading-5">
              {toPercentage(
                +info.getValue(),
                +totalsMetricReport.sales.primaryValue
              )}
            </span>
          ) : (
            <></>
          )}
          {displayComparisonAttributionModelData ? (
            <>
              <br />
              <span className="text-xs">
                {Intl.NumberFormat('en-GB', {
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </span>
            </>
          ) : (
            <></>
          )}
        </span>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.revenue, {
      id: MetricTypes.REVENUE_VALUES,
      header: () =>
        createHeader(
          'Revenue',
          isMainRevenueDataFetching || isCompareRevenueDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm">
            <CurrencyDisplayWrapper currencyCode={currencyCode}>
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))}
            </CurrencyDisplayWrapper>
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs">
              <CurrencyDisplayWrapper currencyCode={currencyCode}>
                {Intl.NumberFormat('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </CurrencyDisplayWrapper>
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.cost, {
      id: MetricTypes.COST,
      header: () =>
        createHeader(
          'Cost',
          isMainCostDataFetching || isCompareCostDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm">
            <CurrencyDisplayWrapper currencyCode={currencyCode}>
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))}
            </CurrencyDisplayWrapper>
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs">
              <CurrencyDisplayWrapper currencyCode={currencyCode}>
                {Intl.NumberFormat('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </CurrencyDisplayWrapper>
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.cpa, {
      id: MetricTypes.CPA,
      header: () =>
        createHeader(
          'CPA',
          isMainCostDataFetching ||
            isMainFormsDataFetching ||
            isMainCallsDataFetching ||
            isCompareCostDataFetching ||
            isCompareFormsDataFetching ||
            isCompareCallsDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm">
            <CurrencyDisplayWrapper currencyCode={currencyCode}>
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))}
            </CurrencyDisplayWrapper>
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs">
              <CurrencyDisplayWrapper currencyCode={currencyCode}>
                {Intl.NumberFormat('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </CurrencyDisplayWrapper>
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.cps, {
      id: MetricTypes.CPS,
      header: () =>
        createHeader(
          'CPS',
          isMainCostDataFetching ||
            isMainSalesDataFetching ||
            isCompareCostDataFetching ||
            isCompareSalesDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm">
            <CurrencyDisplayWrapper currencyCode={currencyCode}>
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))}
            </CurrencyDisplayWrapper>
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs">
              <CurrencyDisplayWrapper currencyCode={currencyCode}>
                {Intl.NumberFormat('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </CurrencyDisplayWrapper>
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.roi, {
      id: MetricTypes.ROI,
      header: () =>
        createHeader(
          'ROI',
          isMainCostDataFetching ||
            isMainRevenueDataFetching ||
            isCompareCostDataFetching ||
            isCompareRevenueDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm">
            <CurrencyDisplayWrapper currencyCode={currencyCode}>
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().primaryValue))}
            </CurrencyDisplayWrapper>
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs">
              <CurrencyDisplayWrapper currencyCode={currencyCode}>
                {Intl.NumberFormat('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(setZeroAsDefault(info.getValue().compareValue))}
              </CurrencyDisplayWrapper>
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    }),
    reportColumnHelper.accessor((row) => row.roas, {
      id: MetricTypes.ROAS,
      header: () =>
        createHeader(
          'ROAS',
          isMainCostDataFetching ||
            isMainRevenueDataFetching ||
            isCompareCostDataFetching ||
            isCompareRevenueDataFetching
        ),
      enableColumnFilter: false,
      cell: (info) => (
        <>
          <span className="text-sm truncate block">
            {Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(setZeroAsDefault(info.getValue().primaryValue))}
          </span>
          {displayComparisonAttributionModelData ? (
            <span className="text-xs truncate block">
              {Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(setZeroAsDefault(info.getValue().compareValue))}
            </span>
          ) : (
            <></>
          )}
        </>
      ),
      size: defaultColumnWidth
    })
  ].filter((column) => column !== undefined) as any[];
};

function toPercentage(value: number, total: number): string {
  const percentageValue = (value / total) * 100;

  if (isNaN(percentageValue)) {
    return '';
  }

  return `(${percentageValue.toFixed(2)}%)`;
}
